import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import sqfTrainingService from '../../services/sqfTrainingService';
import sqfService from '../../services/sqfService';
import { useLanguage, t } from '../../context/LanguageContext';
import SqfNav from '../../components/SqfNav';

const CATEGORY_LABELS = {
  food_safety:  { en: 'Food Safety',   es: 'Inocuidad Alimentaria' },
  gmp:          { en: 'GMP',           es: 'BPM'                   },
  allergen:     { en: 'Allergen',      es: 'Alérgenos'             },
  sanitation:   { en: 'Sanitation',    es: 'Saneamiento'           },
  pest_control: { en: 'Pest Control',  es: 'Control de Plagas'     },
  traceability: { en: 'Traceability',  es: 'Trazabilidad'          },
  emergency:    { en: 'Emergency',     es: 'Emergencias'           },
  other:        { en: 'Other',         es: 'Otro'                  },
};

const TYPE_LABELS = {
  single:     { en: 'Single Choice',   es: 'Opción Única'     },
  multi:      { en: 'Multiple Choice', es: 'Opción Múltiple'  },
  true_false: { en: 'True / False',    es: 'Verdadero / Falso'},
};

function uid() { return Math.random().toString(36).slice(2, 9); }

function defaultQuestion() {
  return {
    id: 'q' + uid(),
    type: 'single',
    text: '',
    options: [
      { id: 'o' + uid(), text: '' },
      { id: 'o' + uid(), text: '' },
    ],
    correctIds: [],
    explanation: '',
  };
}

function trueFalseQuestion(lang) {
  return {
    id: 'q' + uid(),
    type: 'true_false',
    text: '',
    options: [
      { id: 'true',  text: t('True', 'Verdadero', lang)  },
      { id: 'false', text: t('False', 'Falso', lang) },
    ],
    correctIds: [],
    explanation: '',
  };
}

// ─── Question Editor ───────────────────────────────────────────────────────────

function QuestionEditor({ question, index, onChange, onDelete, onRegenerate, regenerating, lang, isBilingual }) {
  const update = (key, val) => onChange({ ...question, [key]: val });

  const handleTypeChange = (newType) => {
    if (newType === 'true_false') {
      onChange({
        ...question,
        type: 'true_false',
        options: [
          { id: 'true',  text: t('True', 'Verdadero', lang), textEs: 'Verdadero'  },
          { id: 'false', text: t('False', 'Falso', lang),    textEs: 'Falso' },
        ],
        correctIds: [],
      });
    } else {
      onChange({ ...question, type: newType, correctIds: [] });
    }
  };

  const updateOption = (optId, text) => {
    update('options', question.options.map(o => o.id === optId ? { ...o, text } : o));
  };

  const updateOptionEs = (optId, textEs) => {
    update('options', question.options.map(o => o.id === optId ? { ...o, textEs } : o));
  };

  const addOption = () => {
    update('options', [...question.options, { id: 'o' + uid(), text: '' }]);
  };

  const removeOption = (optId) => {
    const updated = question.options.filter(o => o.id !== optId);
    update('options', updated);
    update('correctIds', question.correctIds.filter(id => id !== optId));
  };

  const toggleCorrect = (optId) => {
    if (question.type === 'single' || question.type === 'true_false') {
      update('correctIds', [optId]);
    } else {
      const already = question.correctIds.includes(optId);
      update('correctIds', already
        ? question.correctIds.filter(id => id !== optId)
        : [...question.correctIds, optId]);
    }
  };

  const isCorrect = (optId) => question.correctIds.includes(optId);

  return (
    <div className="border rounded p-3 mb-3 bg-white">
      <div className="d-flex justify-content-between align-items-start mb-2">
        <span className="fw-semibold text-muted" style={{ fontSize: 12 }}>
          {t('Q', 'P', lang)}{index + 1}
        </span>
        <div className="d-flex gap-1">
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={onRegenerate}
            disabled={regenerating}
            title={t('Regenerate with AI', 'Regenerar con IA', lang)}
          >
            {regenerating ? <span className="spinner-border spinner-border-sm" /> : '✨'}
          </button>
          <button className="btn btn-outline-danger btn-sm" onClick={onDelete}>×</button>
        </div>
      </div>

      <div className="mb-2">
        <div className="d-flex gap-2 mb-2">
          <select
            className="form-select form-select-sm"
            style={{ width: 180 }}
            value={question.type}
            onChange={e => handleTypeChange(e.target.value)}
          >
            {Object.entries(TYPE_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{t(v.en, v.es, lang)}</option>
            ))}
          </select>
          {question.type === 'multi' && (
            <span className="badge bg-info text-dark align-self-center" style={{ fontSize: 10 }}>
              {t('Select all that apply', 'Selecciona todas las que apliquen', lang)}
            </span>
          )}
        </div>
        {isBilingual && <div style={{ fontSize: 10, fontWeight: 600, color: '#555', marginBottom: 2 }}>EN</div>}
        <textarea
          className="form-control form-control-sm"
          rows={2}
          placeholder={t('Question text…', 'Texto de la pregunta…', lang)}
          value={question.text}
          onChange={e => update('text', e.target.value)}
        />
        {isBilingual && (
          <>
            <div style={{ fontSize: 10, fontWeight: 600, color: '#2e7d32', marginTop: 4, marginBottom: 2 }}>ES</div>
            <textarea
              className="form-control form-control-sm"
              rows={2}
              placeholder="Texto de la pregunta en español…"
              value={question.textEs || ''}
              onChange={e => update('textEs', e.target.value)}
              style={{ background: '#f6fdf8', borderColor: '#c8e6c9' }}
            />
          </>
        )}
      </div>

      <div className="mb-2">
        <div className="text-muted mb-1" style={{ fontSize: 11 }}>
          {t('Options — click ✓ to mark correct', 'Opciones — haz clic en ✓ para marcar correcta', lang)}
        </div>
        {question.options.map((opt, i) => (
          <div key={opt.id} className="mb-1">
            <div className="d-flex align-items-center gap-2">
              <button
                className={`btn btn-sm ${isCorrect(opt.id) ? 'btn-success' : 'btn-outline-secondary'}`}
                style={{ width: 32, padding: '1px 4px', fontSize: 13 }}
                onClick={() => toggleCorrect(opt.id)}
                title={t('Mark as correct', 'Marcar como correcta', lang)}
              >
                {isCorrect(opt.id) ? '✓' : '○'}
              </button>
              {isBilingual && <span style={{ fontSize: 10, fontWeight: 600, color: '#555', flexShrink: 0, width: 20 }}>EN</span>}
              <input
                className="form-control form-control-sm"
                value={opt.text}
                onChange={e => updateOption(opt.id, e.target.value)}
                placeholder={`${t('Option', 'Opción', lang)} ${i + 1}`}
                disabled={question.type === 'true_false'}
              />
              {question.type !== 'true_false' && question.options.length > 2 && (
                <button
                  className="btn btn-outline-danger btn-sm"
                  style={{ padding: '1px 6px' }}
                  onClick={() => removeOption(opt.id)}
                >×</button>
              )}
            </div>
            {isBilingual && (
              <div className="d-flex align-items-center gap-2 mt-1" style={{ paddingLeft: 40 }}>
                <span style={{ fontSize: 10, fontWeight: 600, color: '#2e7d32', flexShrink: 0, width: 20 }}>ES</span>
                <input
                  className="form-control form-control-sm"
                  value={opt.textEs || ''}
                  onChange={e => updateOptionEs(opt.id, e.target.value)}
                  placeholder={`Opción ${i + 1}`}
                  disabled={question.type === 'true_false'}
                  style={{ background: '#f6fdf8', borderColor: '#c8e6c9' }}
                />
              </div>
            )}
          </div>
        ))}
        {question.type !== 'true_false' && (
          <button
            className="btn btn-outline-secondary btn-sm mt-1"
            style={{ fontSize: 12 }}
            onClick={addOption}
          >
            + {t('Add option', 'Agregar opción', lang)}
          </button>
        )}
      </div>

      <div>
        {isBilingual && <div style={{ fontSize: 10, fontWeight: 600, color: '#555', marginBottom: 2 }}>EN</div>}
        <input
          className="form-control form-control-sm"
          placeholder={t('Explanation (shown after attempt) — optional', 'Explicación (se muestra tras el intento) — opcional', lang)}
          value={question.explanation}
          onChange={e => update('explanation', e.target.value)}
        />
        {isBilingual && (
          <>
            <div style={{ fontSize: 10, fontWeight: 600, color: '#2e7d32', marginTop: 4, marginBottom: 2 }}>ES</div>
            <input
              className="form-control form-control-sm"
              placeholder="Explicación en español (opcional)…"
              value={question.explanationEs || ''}
              onChange={e => update('explanationEs', e.target.value)}
              style={{ background: '#f6fdf8', borderColor: '#c8e6c9' }}
            />
          </>
        )}
      </div>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────

export default function SqfTrainingBuilderPage() {
  const { id } = useParams();
  const { lang } = useLanguage();
  const [training, setTraining] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saveMsg, setSaveMsg] = useState('');

  // Active draft version
  const [version, setVersion] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [material, setMaterial] = useState([]); // [{ id, title, body, titleEs?, bodyEs? }]
  const [activeTab, setActiveTab] = useState('questions'); // 'questions' | 'material'
  const [settings, setSettings] = useState({ passingScore: 80, maxAttempts: null, allowReview: true, estimatedMinutes: null });
  const [saving, setSaving] = useState(false);
  const isBilingual = questions.some(q => q.textEs) || material.some(s => s.titleEs || s.bodyEs);
  const [publishing, setPublishing] = useState(false);

  // Regenerate per-question
  const [regeneratingIdx, setRegeneratingIdx] = useState(null);
  const [regeneratingMaterial, setRegeneratingMaterial] = useState(false);

  // Material preview — shows the slide deck exactly as the employee will see it in the portal,
  // before any version with this material is ever published/assigned.
  const [showMaterialPreview, setShowMaterialPreview] = useState(false);
  const [previewIdx, setPreviewIdx] = useState(0);
  const [previewLang, setPreviewLang] = useState(lang || 'en');

  // AI generation modal
  const [showAiModal, setShowAiModal] = useState(false);
  const [aiTopic, setAiTopic] = useState('');
  const [aiCount, setAiCount] = useState(10);
  const [aiDifficulty, setAiDifficulty] = useState('intermediate');
  const [aiLanguage, setAiLanguage] = useState(lang || 'en');
  const [aiClauseIds, setAiClauseIds] = useState([]);
  const [aiClauseSearch, setAiClauseSearch] = useState('');
  const [aiIncludeMaterial, setAiIncludeMaterial] = useState(true);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiError, setAiError] = useState('');

  // Confirmation modal
  const [confirmModal, setConfirmModal] = useState(null);

  // Clause links
  const [clauses, setClauses] = useState([]);
  const [availableClauses, setAvailableClauses] = useState([]);
  const [addingClause, setAddingClause] = useState(false);
  const [clauseSearch, setClauseSearch] = useState('');
  const [showClausePanel, setShowClausePanel] = useState(false);

  // Training metadata editing
  const [editTitle, setEditTitle] = useState('');
  const [editCategory, setEditCategory] = useState('gmp');
  const [editDesc, setEditDesc] = useState('');
  const [savingMeta, setSavingMeta] = useState(false);

  useEffect(() => {
    Promise.all([
      sqfTrainingService.getTraining(id),
      sqfTrainingService.getTrainingClauses(id),
    ]).then(([tr, cls]) => {
      setTraining(tr);
      setEditTitle(tr.title);
      setEditCategory(tr.category || 'gmp');
      setEditDesc(tr.description || '');
      setClauses(cls || []);

      const draft = (tr.Versions || []).find(v => v.status === 'draft');
      if (draft) {
        setVersion(draft);
        setQuestions(draft.questionsJson || []);
        setMaterial(draft.materialJson || []);
        setSettings({
          passingScore: draft.passingScore ?? 80,
          maxAttempts: draft.maxAttempts ?? null,
          allowReview: draft.allowReview ?? true,
          estimatedMinutes: draft.estimatedMinutes ?? null,
        });
      }
    })
      .catch(() => setError(t('Failed to load training.', 'Error al cargar la capacitación.', lang)))
      .finally(() => setLoading(false));
  }, [id]); // eslint-disable-line

  const loadClauses = useCallback(async () => {
    try {
      const all = await sqfService.getClauses?.() || [];
      setAvailableClauses(all);
    } catch {
      /* clauses optional */
    }
  }, []);

  useEffect(() => {
    if ((showClausePanel || showAiModal) && availableClauses.length === 0) loadClauses();
  }, [showClausePanel, showAiModal, availableClauses.length, loadClauses]);

  const handleSaveDraft = async () => {
    if (!version) return;
    setSaving(true);
    setSaveMsg('');
    try {
      const updated = await sqfTrainingService.updateVersion(id, version.id, {
        questionsJson: questions,
        materialJson: material,
        ...settings,
      });
      setVersion(updated);
      setSaveMsg(t('Saved.', 'Guardado.', lang));
      setTimeout(() => setSaveMsg(''), 2000);
    } catch {
      setError(t('Failed to save draft.', 'Error al guardar el borrador.', lang));
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!version) return;
    if (!questions.length) return setError(t('Add at least one question before publishing.', 'Agrega al menos una pregunta antes de publicar.', lang));
    const hasUnanswered = questions.some(q => !q.correctIds?.length);
    if (hasUnanswered) return setError(t('All questions must have at least one correct answer marked.', 'Todas las preguntas deben tener al menos una respuesta correcta marcada.', lang));
    setConfirmModal({
      title: t('Publish Version', 'Publicar Versión', lang),
      message: t('Publish this version? The previous active version will be archived and this version will be live for assignments.', '¿Publicar esta versión? La versión activa anterior será archivada y esta versión estará disponible para asignaciones.', lang),
      confirmLabel: t('Publish', 'Publicar', lang),
      variant: 'success',
      onConfirm: async () => {
        setPublishing(true);
        try {
          await handleSaveDraft();
          await sqfTrainingService.publishVersion(id, version.id);
          const updated = await sqfTrainingService.getTraining(id);
          setTraining(updated);
          const newDraft = (updated.Versions || []).find(v => v.status === 'draft');
          if (newDraft) {
            setVersion(newDraft);
            setQuestions(newDraft.questionsJson || []);
            setMaterial(newDraft.materialJson || []);
          }
          setSaveMsg(t('Published!', '¡Publicado!', lang));
          setTimeout(() => setSaveMsg(''), 3000);
        } catch {
          setError(t('Failed to publish version.', 'Error al publicar la versión.', lang));
        } finally {
          setPublishing(false);
        }
      },
    });
  };

  const handleDeleteDraft = () => {
    if (!version) return;
    setConfirmModal({
      title: t('Delete Draft', 'Eliminar Borrador', lang),
      message: t(
        `Delete draft v${version.versionNumber}? All unsaved questions will be permanently removed. This cannot be undone.`,
        `¿Eliminar el borrador v${version.versionNumber}? Todas las preguntas no guardadas se eliminarán permanentemente. Esta acción no se puede deshacer.`,
        lang
      ),
      confirmLabel: t('Delete Draft', 'Eliminar Borrador', lang),
      variant: 'danger',
      onConfirm: async () => {
        try {
          await sqfTrainingService.deleteVersion(id, version.id);
          const updated = await sqfTrainingService.getTraining(id);
          setTraining(updated);
          setVersion(null);
          setQuestions([]);
          setMaterial([]);
          setSettings({ passingScore: 80, maxAttempts: null, allowReview: true, estimatedMinutes: null });
          setSaveMsg(t('Draft deleted.', 'Borrador eliminado.', lang));
          setTimeout(() => setSaveMsg(''), 2000);
        } catch {
          setError(t('Failed to delete draft.', 'Error al eliminar el borrador.', lang));
        }
      },
    });
  };

  const handleCreateNewVersion = async () => {
    setConfirmModal({
      title: t('New Version', 'Nueva Versión', lang),
      message: t('Create a new draft version cloned from the current published version? You can edit it before publishing.', '¿Crear una nueva versión borrador clonada de la versión publicada actual? Puedes editarla antes de publicar.', lang),
      confirmLabel: t('Create', 'Crear', lang),
      variant: 'primary',
      onConfirm: async () => {
        try {
          const newVersion = await sqfTrainingService.createVersion(id, {});
          setVersion(newVersion);
          setQuestions(newVersion.questionsJson || []);
          setMaterial(newVersion.materialJson || []);
          setSaveMsg(t('New draft version created.', 'Nueva versión borrador creada.', lang));
          setTimeout(() => setSaveMsg(''), 2000);
        } catch {
          setError(t('Failed to create new version.', 'Error al crear nueva versión.', lang));
        }
      },
    });
  };

  const handleSaveMeta = async () => {
    setSavingMeta(true);
    try {
      const updated = await sqfTrainingService.updateTraining(id, {
        title: editTitle,
        category: editCategory,
        description: editDesc,
      });
      setTraining(tr => ({ ...tr, ...updated }));
      setSaveMsg(t('Info saved.', 'Información guardada.', lang));
      setTimeout(() => setSaveMsg(''), 2000);
    } catch {
      setError(t('Failed to save info.', 'Error al guardar la información.', lang));
    } finally {
      setSavingMeta(false);
    }
  };

  // Questions helpers
  const addQuestion = (type) => {
    const q = type === 'true_false' ? trueFalseQuestion(lang) : { ...defaultQuestion(), type };
    setQuestions(qs => [...qs, q]);
  };

  const updateQuestion = (idx, q) => setQuestions(qs => qs.map((old, i) => i === idx ? q : old));
  const deleteQuestion = (idx) => setQuestions(qs => qs.filter((_, i) => i !== idx));
  const moveQuestion = (idx, dir) => {
    setQuestions(qs => {
      const arr = [...qs];
      const swap = idx + dir;
      if (swap < 0 || swap >= arr.length) return arr;
      [arr[idx], arr[swap]] = [arr[swap], arr[idx]];
      return arr;
    });
  };

  // AI generation
  const handleAiGenerate = async () => {
    if (!aiTopic.trim()) return;
    setAiGenerating(true);
    setAiError('');
    try {
      const result = await sqfTrainingService.generateTraining({
        topic: aiTopic,
        numQuestions: aiCount,
        difficulty: aiDifficulty,
        language: aiLanguage,
        includeMaterial: aiIncludeMaterial,
        ...(aiClauseIds.length && { clauseIds: aiClauseIds }),
      });
      const generated = result.training || result;
      if (generated.questions?.length) {
        setQuestions(qs => [...qs, ...generated.questions]);
      }
      if (aiIncludeMaterial && generated.material?.length) {
        setMaterial(m => [...m, ...generated.material]);
        setActiveTab('material');
      }
      if (!editTitle && generated.title) setEditTitle(generated.title);
      if (!editDesc && generated.description) setEditDesc(generated.description);
      setShowAiModal(false);
      setAiTopic('');
      setAiClauseIds([]);
      setAiClauseSearch('');
    } catch (err) {
      setAiError(err?.response?.data?.message || t('AI generation failed.', 'Error en la generación con IA.', lang));
    } finally {
      setAiGenerating(false);
    }
  };

  const handleRegenerateMaterial = async () => {
    setRegeneratingMaterial(true);
    setError('');
    try {
      const result = await sqfTrainingService.generateTraining({
        topic: editTitle || 'food safety',
        numQuestions: 1,
        difficulty: aiDifficulty || 'intermediate',
        language: aiLanguage,
        includeMaterial: true,
      });
      const generated = result.training || result;
      if (generated.material?.length) setMaterial(generated.material);
    } catch {
      setError(t('Failed to regenerate material.', 'Error al regenerar el material.', lang));
    } finally {
      setRegeneratingMaterial(false);
    }
  };

  const addSlide = () => setMaterial(m => [...m, { id: 's' + uid(), title: '', body: '' }]);
  const updateSlide = (idx, patch) => setMaterial(m => m.map((s, i) => i === idx ? { ...s, ...patch } : s));
  const deleteSlide = (idx) => setMaterial(m => m.filter((_, i) => i !== idx));
  const moveSlide = (idx, dir) => {
    setMaterial(m => {
      const arr = [...m];
      const swap = idx + dir;
      if (swap < 0 || swap >= arr.length) return arr;
      [arr[idx], arr[swap]] = [arr[swap], arr[idx]];
      return arr;
    });
  };

  const handleRegenerate = async (idx) => {
    setRegeneratingIdx(idx);
    try {
      const newQ = await sqfTrainingService.regenerateQuestion({
        topic: editTitle || 'food safety',
        trainingTitle: editTitle,
        existingQuestions: questions.map(q => q.text),
        questionIndex: idx,
        difficulty: aiDifficulty || 'intermediate',
      });
      if (newQ) {
        updateQuestion(idx, { ...questions[idx], ...newQ, id: questions[idx].id });
      }
    } catch {
      setError(t('Failed to regenerate question.', 'Error al regenerar la pregunta.', lang));
    } finally {
      setRegeneratingIdx(null);
    }
  };

  // Clause link
  const handleAddClause = async (clauseId) => {
    setAddingClause(true);
    try {
      const result = await sqfTrainingService.addTrainingClause(id, { clauseId });
      if (result.clause) setClauses(cs => [...cs, result.clause]);
      setShowClausePanel(false);
      setClauseSearch('');
    } catch {
      setError(t('Failed to link clause.', 'Error al vincular la cláusula.', lang));
    } finally {
      setAddingClause(false);
    }
  };

  const handleRemoveClause = async (clauseId) => {
    try {
      await sqfTrainingService.removeTrainingClause(id, clauseId);
      setClauses(cs => cs.filter(c => c.id !== clauseId));
    } catch {
      setError(t('Failed to remove clause link.', 'Error al eliminar el vínculo de cláusula.', lang));
    }
  };

  const filteredClauses = availableClauses.filter(c =>
    !clauses.find(linked => linked.id === c.id) &&
    (clauseSearch === '' ||
      c.clauseRef?.toLowerCase().includes(clauseSearch.toLowerCase()) ||
      c.title?.toLowerCase().includes(clauseSearch.toLowerCase()))
  );

  const publishedVersion = (training?.Versions || []).find(v => v.status === 'published');
  const noDraftVersion = !version;

  if (loading) {
    return (
      <>
        <SqfNav />
        <div className="text-center py-5 text-muted">
          <div className="spinner-border spinner-border-sm me-2" />{t('Loading…', 'Cargando…', lang)}
        </div>
      </>
    );
  }

  if (!training) {
    return (
      <>
        <SqfNav />
        <div className="container mt-4">
          <div className="alert alert-danger">{t('Training not found.', 'Capacitación no encontrada.', lang)}</div>
          <Link to="/sqf/training" className="btn btn-secondary btn-sm">{t('Back', 'Regresar', lang)}</Link>
        </div>
      </>
    );
  }

  return (
    <>
      <SqfNav />
      <div className="container-fluid mt-3 pb-5" style={{ maxWidth: 1100 }}>
        {/* Breadcrumb + header */}
        <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
          <div>
            <Link to="/sqf/training" className="text-muted" style={{ fontSize: 12 }}>← {t('Training Library', 'Biblioteca de Capacitaciones', lang)}</Link>
            <h5 className="mb-0 mt-1">{training.title}</h5>
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            {saveMsg && <span className="text-success" style={{ fontSize: 13 }}>{saveMsg}</span>}
            {error && (
              <span className="text-danger" style={{ fontSize: 13 }}>
                {error}
                <button className="btn-close ms-1" style={{ fontSize: 10 }} onClick={() => setError('')} />
              </span>
            )}
            {version && (
              <>
                <button className="btn btn-outline-danger btn-sm" onClick={handleDeleteDraft}>
                  {t('Delete Draft', 'Eliminar Borrador', lang)}
                </button>
                <button className="btn btn-outline-secondary btn-sm" onClick={handleSaveDraft} disabled={saving}>
                  {saving ? t('Saving…', 'Guardando…', lang) : t('Save Draft', 'Guardar Borrador', lang)}
                </button>
                <button className="btn btn-success btn-sm" onClick={handlePublish} disabled={publishing || saving}>
                  {publishing
                    ? t('Publishing…', 'Publicando…', lang)
                    : `${t('Publish', 'Publicar', lang)} v${version.versionNumber}`}
                </button>
              </>
            )}
            {noDraftVersion && publishedVersion && (
              <button className="btn btn-outline-primary btn-sm" onClick={handleCreateNewVersion}>
                + {t('New Version', 'Nueva Versión', lang)}
              </button>
            )}
          </div>
        </div>

        <div className="row g-3">
          {/* LEFT: Question list */}
          <div className="col-12 col-lg-8">
            {/* Version status banner */}
            {noDraftVersion ? (
              <div className="alert alert-info d-flex align-items-center justify-content-between py-2 mb-3">
                <span>
                  {publishedVersion
                    ? t(`v${publishedVersion.versionNumber} is published. Create a new draft to make changes.`,
                        `v${publishedVersion.versionNumber} está publicada. Crea un nuevo borrador para hacer cambios.`, lang)
                    : t('No version yet.', 'Aún no hay versión.', lang)}
                </span>
                {publishedVersion && (
                  <button className="btn btn-sm btn-outline-primary ms-3" onClick={handleCreateNewVersion}>
                    + {t('New Version', 'Nueva Versión', lang)}
                  </button>
                )}
              </div>
            ) : (
              <div className="alert alert-warning d-flex align-items-center justify-content-between py-2 mb-3">
                <div className="d-flex align-items-center gap-2">
                  <span className="fw-semibold" style={{ fontSize: 13 }}>
                    {t('Editing draft', 'Editando borrador', lang)} v{version.versionNumber}
                    {publishedVersion && ` (v${publishedVersion.versionNumber} ${t('is active', 'está activa', lang)})`}
                  </span>
                  {isBilingual && (
                    <span className="badge" style={{ background: '#e8f5e9', color: '#2e7d32', border: '1px solid #c8e6c9', fontSize: 10, fontWeight: 600 }}>
                      🌐 EN+ES
                    </span>
                  )}
                </div>
                <span className="text-muted" style={{ fontSize: 12 }}>
                  {questions.length} {t('question', 'pregunta', lang)}{questions.length !== 1 ? (lang === 'es' ? 's' : 's') : ''}
                </span>
              </div>
            )}

            {/* Questions / Material tab switcher */}
            {version && (
              <ul className="nav nav-tabs mb-3" style={{ fontSize: 13 }}>
                <li className="nav-item">
                  <button className={`nav-link ${activeTab === 'questions' ? 'active' : ''}`} onClick={() => setActiveTab('questions')}>
                    ❓ {t('Questions', 'Preguntas', lang)} ({questions.length})
                  </button>
                </li>
                <li className="nav-item">
                  <button className={`nav-link ${activeTab === 'material' ? 'active' : ''}`} onClick={() => setActiveTab('material')}>
                    📖 {t('Material', 'Material', lang)} {material.length > 0 ? `(${material.length})` : ''}
                  </button>
                </li>
              </ul>
            )}

            {activeTab === 'questions' && (
              <>
                {/* AI generate + add buttons */}
                {version && (
                  <div className="d-flex gap-2 mb-3 flex-wrap">
                    <button className="btn btn-outline-primary btn-sm" onClick={() => setShowAiModal(true)}>
                      ✨ {t('Generate with AI', 'Generar con IA', lang)}
                    </button>
                    <button className="btn btn-outline-secondary btn-sm" onClick={() => addQuestion('single')}>
                      + {t('Single Choice', 'Opción Única', lang)}
                    </button>
                    <button className="btn btn-outline-secondary btn-sm" onClick={() => addQuestion('multi')}>
                      + {t('Multiple Choice', 'Opción Múltiple', lang)}
                    </button>
                    <button className="btn btn-outline-secondary btn-sm" onClick={() => addQuestion('true_false')}>
                      + {t('True/False', 'Verdadero/Falso', lang)}
                    </button>
                  </div>
                )}

                {/* Empty state */}
                {questions.length === 0 && version && (
                  <div className="text-center py-4 text-muted border rounded bg-light">
                    <div style={{ fontSize: 32 }}>❓</div>
                    <p className="mt-2">{t('No questions yet. Add one or use AI generation.', 'Aún no hay preguntas. Agrega una o usa generación con IA.', lang)}</p>
                  </div>
                )}

                {/* Question editors */}
                {questions.map((q, idx) => (
                  <div key={q.id} className="d-flex gap-2 align-items-start">
                    <div className="d-flex flex-column gap-1 mt-2" style={{ flexShrink: 0 }}>
                      <button
                        className="btn btn-outline-secondary btn-sm p-0"
                        style={{ width: 24, height: 24, fontSize: 12 }}
                        onClick={() => moveQuestion(idx, -1)}
                        disabled={idx === 0}
                      >↑</button>
                      <button
                        className="btn btn-outline-secondary btn-sm p-0"
                        style={{ width: 24, height: 24, fontSize: 12 }}
                        onClick={() => moveQuestion(idx, 1)}
                        disabled={idx === questions.length - 1}
                      >↓</button>
                    </div>
                    <div className="flex-grow-1">
                      <QuestionEditor
                        question={q}
                        index={idx}
                        lang={lang}
                        isBilingual={isBilingual}
                        onChange={updated => updateQuestion(idx, updated)}
                        onDelete={() => deleteQuestion(idx)}
                        onRegenerate={() => handleRegenerate(idx)}
                        regenerating={regeneratingIdx === idx}
                      />
                    </div>
                  </div>
                ))}

                {questions.length > 0 && version && (
                  <div className="d-flex gap-2 mt-2 flex-wrap">
                    <button className="btn btn-outline-secondary btn-sm" onClick={() => addQuestion('single')}>
                      + {t('Single Choice', 'Opción Única', lang)}
                    </button>
                    <button className="btn btn-outline-secondary btn-sm" onClick={() => addQuestion('multi')}>
                      + {t('Multiple Choice', 'Opción Múltiple', lang)}
                    </button>
                    <button className="btn btn-outline-secondary btn-sm" onClick={() => addQuestion('true_false')}>
                      + {t('True/False', 'Verdadero/Falso', lang)}
                    </button>
                  </div>
                )}
              </>
            )}

            {activeTab === 'material' && version && (
              <>
                <div className="d-flex gap-2 mb-3 flex-wrap">
                  <button className="btn btn-outline-primary btn-sm" onClick={() => setShowAiModal(true)}>
                    ✨ {t('Generate with AI', 'Generar con IA', lang)}
                  </button>
                  {material.length > 0 && (
                    <button className="btn btn-outline-secondary btn-sm" onClick={handleRegenerateMaterial} disabled={regeneratingMaterial}>
                      {regeneratingMaterial
                        ? <><span className="spinner-border spinner-border-sm me-2" />{t('Regenerating…', 'Regenerando…', lang)}</>
                        : <>🔄 {t('Regenerate Material', 'Regenerar Material', lang)}</>}
                    </button>
                  )}
                  {material.length > 0 && (
                    <button className="btn btn-outline-primary btn-sm" onClick={() => { setPreviewIdx(0); setShowMaterialPreview(true); }}>
                      👁️ {t('Preview', 'Vista Previa', lang)}
                    </button>
                  )}
                  <button className="btn btn-outline-secondary btn-sm" onClick={addSlide}>
                    + {t('Add Slide', 'Agregar Diapositiva', lang)}
                  </button>
                </div>

                {material.length === 0 && (
                  <div className="text-center py-4 text-muted border rounded bg-light">
                    <div style={{ fontSize: 32 }}>📖</div>
                    <p className="mt-2">
                      {t('No training material yet — optional. Add slides manually or generate them with AI.', 'Aún no hay material de capacitación — opcional. Agrega diapositivas manualmente o genéralas con IA.', lang)}
                    </p>
                  </div>
                )}

                {material.map((s, idx) => (
                  <div key={s.id} className="d-flex gap-2 align-items-start mb-2">
                    <div className="d-flex flex-column gap-1 mt-2" style={{ flexShrink: 0 }}>
                      <button
                        className="btn btn-outline-secondary btn-sm p-0"
                        style={{ width: 24, height: 24, fontSize: 12 }}
                        onClick={() => moveSlide(idx, -1)}
                        disabled={idx === 0}
                      >↑</button>
                      <button
                        className="btn btn-outline-secondary btn-sm p-0"
                        style={{ width: 24, height: 24, fontSize: 12 }}
                        onClick={() => moveSlide(idx, 1)}
                        disabled={idx === material.length - 1}
                      >↓</button>
                    </div>
                    <div className="card flex-grow-1">
                      <div className="card-body p-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span className="text-muted fw-semibold" style={{ fontSize: 12 }}>{t('Slide', 'Diapositiva', lang)} {idx + 1}</span>
                          <button className="btn btn-outline-danger btn-sm py-0 px-2" onClick={() => deleteSlide(idx)}>×</button>
                        </div>
                        <input
                          className="form-control form-control-sm mb-2"
                          placeholder={t('Slide title', 'Título de la diapositiva', lang)}
                          value={s.title}
                          onChange={e => updateSlide(idx, { title: e.target.value })}
                        />
                        <textarea
                          className="form-control form-control-sm"
                          rows={4}
                          placeholder={t('Slide body — what the employee should read and learn.', 'Contenido de la diapositiva — lo que el empleado debe leer y aprender.', lang)}
                          value={s.body}
                          onChange={e => updateSlide(idx, { body: e.target.value })}
                        />
                        {isBilingual && (
                          <>
                            <input
                              className="form-control form-control-sm mt-2 mb-2"
                              placeholder={t('Título en español', 'Título en español', lang)}
                              value={s.titleEs || ''}
                              onChange={e => updateSlide(idx, { titleEs: e.target.value })}
                            />
                            <textarea
                              className="form-control form-control-sm"
                              rows={4}
                              placeholder={t('Contenido en español', 'Contenido en español', lang)}
                              value={s.bodyEs || ''}
                              onChange={e => updateSlide(idx, { bodyEs: e.target.value })}
                            />
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                {material.length > 0 && (
                  <button className="btn btn-outline-secondary btn-sm mt-2" onClick={addSlide}>
                    + {t('Add Slide', 'Agregar Diapositiva', lang)}
                  </button>
                )}
              </>
            )}
          </div>

          {/* RIGHT: Settings + metadata + clause links */}
          <div className="col-12 col-lg-4">
            {/* Quiz Settings */}
            {version && (
              <div className="card mb-3">
                <div className="card-header py-2 fw-semibold" style={{ fontSize: 13 }}>
                  {t('Quiz Settings', 'Configuración del Examen', lang)}
                </div>
                <div className="card-body p-3">
                  <div className="mb-2">
                    <label className="form-label mb-1" style={{ fontSize: 12 }}>
                      {t('Passing Score (%)', 'Puntaje de Aprobación (%)', lang)}
                    </label>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      min={1} max={100}
                      value={settings.passingScore}
                      onChange={e => setSettings(s => ({ ...s, passingScore: Number(e.target.value) }))}
                    />
                  </div>
                  <div className="mb-2">
                    <label className="form-label mb-1" style={{ fontSize: 12 }}>
                      {t('Max Attempts (blank = unlimited)', 'Intentos Máximos (vacío = ilimitado)', lang)}
                    </label>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      min={1}
                      value={settings.maxAttempts ?? ''}
                      onChange={e => setSettings(s => ({ ...s, maxAttempts: e.target.value ? Number(e.target.value) : null }))}
                      placeholder={t('Unlimited', 'Ilimitado', lang)}
                    />
                  </div>
                  <div className="mb-2">
                    <label className="form-label mb-1" style={{ fontSize: 12 }}>
                      {t('Estimated Minutes', 'Minutos Estimados', lang)}
                    </label>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      min={1}
                      value={settings.estimatedMinutes ?? ''}
                      onChange={e => setSettings(s => ({ ...s, estimatedMinutes: e.target.value ? Number(e.target.value) : null }))}
                      placeholder="e.g. 15"
                    />
                  </div>
                  <div className="form-check form-switch mb-0">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="allowReview"
                      checked={settings.allowReview}
                      onChange={e => setSettings(s => ({ ...s, allowReview: e.target.checked }))}
                    />
                    <label className="form-check-label" htmlFor="allowReview" style={{ fontSize: 12 }}>
                      {t('Allow review of answers after completion', 'Permitir revisar respuestas tras completar', lang)}
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* Training Metadata */}
            <div className="card mb-3">
              <div className="card-header py-2 fw-semibold" style={{ fontSize: 13 }}>
                {t('Training Info', 'Información de la Capacitación', lang)}
              </div>
              <div className="card-body p-3">
                <div className="mb-2">
                  <label className="form-label mb-1" style={{ fontSize: 12 }}>{t('Title', 'Título', lang)}</label>
                  <input
                    className="form-control form-control-sm"
                    value={editTitle}
                    onChange={e => setEditTitle(e.target.value)}
                  />
                </div>
                <div className="mb-2">
                  <label className="form-label mb-1" style={{ fontSize: 12 }}>{t('Category', 'Categoría', lang)}</label>
                  <select
                    className="form-select form-select-sm"
                    value={editCategory}
                    onChange={e => setEditCategory(e.target.value)}
                  >
                    {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
                      <option key={k} value={k}>{t(v.en, v.es, lang)}</option>
                    ))}
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label mb-1" style={{ fontSize: 12 }}>{t('Description', 'Descripción', lang)}</label>
                  <textarea
                    className="form-control form-control-sm"
                    rows={2}
                    value={editDesc}
                    onChange={e => setEditDesc(e.target.value)}
                  />
                </div>
                <button
                  className="btn btn-outline-secondary btn-sm w-100"
                  onClick={handleSaveMeta}
                  disabled={savingMeta}
                >
                  {savingMeta ? t('Saving…', 'Guardando…', lang) : t('Save Info', 'Guardar Información', lang)}
                </button>
              </div>
            </div>

            {/* SQF Clause Links */}
            <div className="card mb-3">
              <div className="card-header py-2 d-flex justify-content-between align-items-center">
                <span className="fw-semibold" style={{ fontSize: 13 }}>
                  {t('SQF Clause Links', 'Vínculos de Cláusulas SQF', lang)}
                </span>
                <button
                  className="btn btn-outline-secondary btn-sm"
                  style={{ fontSize: 11 }}
                  onClick={() => setShowClausePanel(p => !p)}
                >
                  {showClausePanel ? t('Done', 'Listo', lang) : `+ ${t('Link Clause', 'Vincular Cláusula', lang)}`}
                </button>
              </div>
              <div className="card-body p-3">
                {clauses.length === 0 && !showClausePanel && (
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    {t('No clauses linked yet.', 'Aún no hay cláusulas vinculadas.', lang)}
                  </div>
                )}
                {clauses.map(c => (
                  <div key={c.id} className="d-flex align-items-center justify-content-between mb-1">
                    <span style={{ fontSize: 12 }}>
                      <strong>{c.clauseRef}</strong> — {c.title}
                    </span>
                    <button
                      className="btn btn-outline-danger btn-sm p-0 px-1"
                      style={{ fontSize: 11 }}
                      onClick={() => handleRemoveClause(c.id)}
                    >×</button>
                  </div>
                ))}
                {showClausePanel && (
                  <div className="mt-2">
                    <input
                      className="form-control form-control-sm mb-2"
                      placeholder={t('Search clauses…', 'Buscar cláusulas…', lang)}
                      value={clauseSearch}
                      onChange={e => setClauseSearch(e.target.value)}
                      autoFocus
                    />
                    <div style={{ maxHeight: 180, overflowY: 'auto' }}>
                      {filteredClauses.slice(0, 30).map(c => (
                        <button
                          key={c.id}
                          className="btn btn-outline-secondary btn-sm w-100 text-start mb-1"
                          style={{ fontSize: 11 }}
                          onClick={() => handleAddClause(c.id)}
                          disabled={addingClause}
                        >
                          <strong>{c.clauseRef}</strong> — {c.title}
                        </button>
                      ))}
                      {filteredClauses.length === 0 && (
                        <div className="text-muted" style={{ fontSize: 12 }}>
                          {t('No results.', 'Sin resultados.', lang)}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Version history */}
            {(training?.Versions || []).length > 0 && (
              <div className="card">
                <div className="card-header py-2 fw-semibold" style={{ fontSize: 13 }}>
                  {t('Version History', 'Historial de Versiones', lang)}
                </div>
                <ul className="list-group list-group-flush">
                  {(training.Versions || []).sort((a, b) => b.versionNumber - a.versionNumber).map(v => (
                    <li key={v.id} className="list-group-item py-1 px-3 d-flex justify-content-between align-items-center" style={{ fontSize: 12 }}>
                      <span>v{v.versionNumber}</span>
                      <span className={`badge bg-${v.status === 'published' ? 'success' : v.status === 'draft' ? 'warning text-dark' : 'secondary'}`}>
                        {v.status === 'published' ? t('published', 'publicado', lang)
                          : v.status === 'draft' ? t('draft', 'borrador', lang)
                          : t('archived', 'archivado', lang)}
                      </span>
                      {v.publishedAt && (
                        <span className="text-muted">{new Date(v.publishedAt).toLocaleDateString()}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Material Preview Modal — exactly what the employee sees in the portal before the quiz */}
      {showMaterialPreview && material.length > 0 && (() => {
        const slide = material[previewIdx];
        const isLastSlide = previewIdx === material.length - 1;
        const slideTitle = (previewLang === 'es' && slide.titleEs) ? slide.titleEs : slide.title;
        const slideBody = (previewLang === 'es' && slide.bodyEs) ? slide.bodyEs : slide.body;
        return (
          <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 480 }}>
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">👁️ {t('Material Preview', 'Vista Previa del Material', lang)}</h5>
                  <button className="btn-close" onClick={() => setShowMaterialPreview(false)} />
                </div>
                <div className="modal-body">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="text-muted" style={{ fontSize: 12 }}>
                      📖 {t('Slide', 'Diapositiva', lang)} {previewIdx + 1} / {material.length}
                    </span>
                    {isBilingual && (
                      <div className="btn-group btn-group-sm">
                        <button className={`btn btn-outline-secondary ${previewLang === 'en' ? 'active' : ''}`} onClick={() => setPreviewLang('en')} style={{ fontSize: 11 }}>EN</button>
                        <button className={`btn btn-outline-secondary ${previewLang === 'es' ? 'active' : ''}`} onClick={() => setPreviewLang('es')} style={{ fontSize: 11 }}>ES</button>
                      </div>
                    )}
                  </div>
                  <div className="progress mb-3" style={{ height: 6 }}>
                    <div className="progress-bar bg-info" style={{ width: `${Math.round(((previewIdx + 1) / material.length) * 100)}%` }} />
                  </div>
                  <div className="card shadow-sm mb-3">
                    <div className="card-body p-4">
                      <h5 className="fw-bold mb-3">{slideTitle || <span className="text-muted fst-italic">{t('(untitled slide)', '(diapositiva sin título)', lang)}</span>}</h5>
                      <p style={{ fontSize: 15, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                        {slideBody || <span className="text-muted fst-italic">{t('(empty slide)', '(diapositiva vacía)', lang)}</span>}
                      </p>
                    </div>
                  </div>
                  <div className="d-flex justify-content-between gap-2">
                    <button className="btn btn-outline-secondary" onClick={() => setPreviewIdx(i => Math.max(0, i - 1))} disabled={previewIdx === 0}>
                      ← {t('Previous', 'Anterior', lang)}
                    </button>
                    {!isLastSlide ? (
                      <button className="btn btn-primary" onClick={() => setPreviewIdx(i => i + 1)}>
                        {t('Next', 'Siguiente', lang)} →
                      </button>
                    ) : (
                      <button className="btn btn-success" onClick={() => setShowMaterialPreview(false)}>
                        ✓ {t('Done Previewing', 'Terminar Vista Previa', lang)}
                      </button>
                    )}
                  </div>
                  <div className="d-flex flex-wrap gap-1 justify-content-center mt-3">
                    {material.map((s, i) => (
                      <button
                        key={s.id}
                        onClick={() => setPreviewIdx(i)}
                        style={{
                          width: 26, height: 26, borderRadius: '50%', border: 'none',
                          fontSize: 10, fontWeight: 600, cursor: 'pointer',
                          background: i === previewIdx ? '#0d6efd' : '#e9ecef',
                          color: i === previewIdx ? '#fff' : '#6c757d',
                        }}
                      >
                        {i + 1}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* AI Generation Modal */}
      {showAiModal && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">✨ {t('Generate Questions with AI', 'Generar Preguntas con IA', lang)}</h5>
                <button className="btn-close" onClick={() => { setShowAiModal(false); setAiError(''); setAiClauseSearch(''); setAiClauseIds([]); setAiLanguage(lang || 'en'); }} />
              </div>
              <div className="modal-body">
                {aiError && <div className="alert alert-danger py-2 mb-3">{aiError}</div>}
                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    {t('Topic', 'Tema', lang)} <span className="text-danger">*</span>
                  </label>
                  <input
                    className="form-control"
                    value={aiTopic}
                    onChange={e => setAiTopic(e.target.value)}
                    placeholder={t('e.g. GMP personal hygiene, allergen cross-contact prevention', 'ej. Higiene personal BPM, prevención de contacto cruzado con alérgenos', lang)}
                    autoFocus
                  />
                  <div className="form-text">{t('Describe what the quiz should cover.', 'Describe qué debe cubrir el examen.', lang)}</div>
                </div>
                <div className="row g-3 mb-3">
                  <div className="col">
                    <label className="form-label fw-semibold">{t('Questions', 'Preguntas', lang)}</label>
                    <select className="form-select" value={aiCount} onChange={e => setAiCount(Number(e.target.value))}>
                      {[5, 8, 10, 12, 15, 20].map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                  <div className="col">
                    <label className="form-label fw-semibold">{t('Difficulty', 'Dificultad', lang)}</label>
                    <select className="form-select" value={aiDifficulty} onChange={e => setAiDifficulty(e.target.value)}>
                      <option value="beginner">{t('Beginner', 'Principiante', lang)}</option>
                      <option value="intermediate">{t('Intermediate', 'Intermedio', lang)}</option>
                      <option value="advanced">{t('Advanced', 'Avanzado', lang)}</option>
                    </select>
                  </div>
                  <div className="col">
                    <label className="form-label fw-semibold">{t('Language', 'Idioma', lang)}</label>
                    <select className="form-select" value={aiLanguage} onChange={e => setAiLanguage(e.target.value)}>
                      <option value="en">{t('English', 'Inglés', lang)}</option>
                      <option value="es">{t('Spanish', 'Español', lang)}</option>
                      <option value="bilingual">{t('Bilingual (EN + ES)', 'Bilingüe (EN + ES)', lang)}</option>
                    </select>
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    {t('SQF Clauses (optional)', 'Cláusulas SQF (opcional)', lang)}
                    {aiClauseIds.length > 0 && (
                      <span className="badge bg-primary ms-2">{aiClauseIds.length} {t('selected', 'seleccionadas', lang)}</span>
                    )}
                  </label>
                  <input
                    className="form-control form-control-sm mb-1"
                    placeholder={t('Search by clause number or name…', 'Buscar por número o nombre de cláusula…', lang)}
                    value={aiClauseSearch}
                    onChange={e => setAiClauseSearch(e.target.value)}
                  />
                  <div
                    className="border rounded p-2"
                    style={{ maxHeight: 160, overflowY: 'auto', background: '#fafafa' }}
                  >
                    {availableClauses.length === 0 ? (
                      <div className="text-muted text-center py-2" style={{ fontSize: 12 }}>
                        {t('Loading clauses…', 'Cargando cláusulas…', lang)}
                      </div>
                    ) : (() => {
                      const term = aiClauseSearch.trim().toLowerCase();
                      const visible = availableClauses.filter(c =>
                        !term ||
                        c.clauseRef?.toLowerCase().includes(term) ||
                        c.title?.toLowerCase().includes(term)
                      );
                      return visible.length === 0 ? (
                        <div className="text-muted text-center py-2" style={{ fontSize: 12 }}>
                          {t('No clauses match.', 'Ninguna cláusula coincide.', lang)}
                        </div>
                      ) : visible.slice(0, 50).map(c => (
                        <div
                          key={c.id}
                          className="form-check"
                          style={{ cursor: 'pointer', padding: '2px 0 2px 26px' }}
                          onClick={() => setAiClauseIds(ids =>
                            ids.includes(c.id) ? ids.filter(i => i !== c.id) : [...ids, c.id]
                          )}
                        >
                          <input
                            className="form-check-input"
                            type="checkbox"
                            readOnly
                            checked={aiClauseIds.includes(c.id)}
                            style={{ cursor: 'pointer' }}
                          />
                          <label className="form-check-label" style={{ cursor: 'pointer', fontSize: 13 }}>
                            <strong>{c.clauseRef}</strong>
                            {c.title && <span className="text-muted ms-1">— {c.title}</span>}
                          </label>
                        </div>
                      ));
                    })()}
                  </div>
                  {aiClauseIds.length > 0 && (
                    <button
                      className="btn btn-link btn-sm p-0 mt-1 text-muted"
                      onClick={() => setAiClauseIds([])}
                      style={{ fontSize: 12 }}
                    >
                      {t('Clear selection', 'Limpiar selección', lang)}
                    </button>
                  )}
                  <div className="form-text">
                    {t('AI will incorporate selected clause requirements into the questions.', 'La IA incorporará los requisitos de las cláusulas seleccionadas en las preguntas.', lang)}
                  </div>
                </div>
                <div className="form-check form-switch mb-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="aiIncludeMaterial"
                    checked={aiIncludeMaterial}
                    onChange={e => setAiIncludeMaterial(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="aiIncludeMaterial" style={{ fontSize: 13 }}>
                    📖 {t('Also generate training material (slides employees review before the quiz)', 'También generar material de capacitación (diapositivas que los empleados revisan antes del examen)', lang)}
                  </label>
                  <div className="form-text">
                    {t('Optional — a short, cheap-to-generate refresher (~$0.05–0.10) the employee reads first. Leave unchecked to generate just the quiz, as before.', 'Opcional — un repaso breve y económico de generar (~$0.05–0.10) que el empleado lee primero. Deja sin marcar para generar solo el examen, como antes.', lang)}
                  </div>
                </div>
                <div className="alert alert-light border py-2" style={{ fontSize: 12 }}>
                  {t('Questions will be appended to the current list. You can edit or delete them after generation.', 'Las preguntas se agregarán a la lista actual. Puedes editarlas o eliminarlas después de la generación.', lang)}
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => { setShowAiModal(false); setAiError(''); setAiClauseSearch(''); setAiClauseIds([]); setAiLanguage(lang || 'en'); }}>
                  {t('Cancel', 'Cancelar', lang)}
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleAiGenerate}
                  disabled={aiGenerating || !aiTopic.trim()}
                >
                  {aiGenerating
                    ? <><span className="spinner-border spinner-border-sm me-2" />{t('Generating…', 'Generando…', lang)}</>
                    : `✨ ${t('Generate', 'Generar', lang)} ${aiCount} ${t('questions', 'preguntas', lang)}${aiIncludeMaterial ? ` + ${t('material', 'material', lang)}` : ''}`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {confirmModal && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 420 }}>
            <div className="modal-content shadow">
              <div className={`modal-header border-bottom-0 pb-0`}>
                <h5 className="modal-title fw-semibold">{confirmModal.title}</h5>
                <button className="btn-close" onClick={() => setConfirmModal(null)} />
              </div>
              <div className="modal-body pt-2">
                <p className="mb-0 text-muted" style={{ fontSize: 14 }}>{confirmModal.message}</p>
              </div>
              <div className="modal-footer border-top-0 pt-0">
                <button className="btn btn-outline-secondary" onClick={() => setConfirmModal(null)}>
                  {t('Cancel', 'Cancelar', lang)}
                </button>
                <button
                  className={`btn btn-${confirmModal.variant || 'primary'}`}
                  onClick={async () => {
                    const fn = confirmModal.onConfirm;
                    setConfirmModal(null);
                    await fn?.();
                  }}
                >
                  {confirmModal.confirmLabel}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
